// components/ScrollRestoration.js
import { useEffect, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ScrollRestoration component
 * A comprehensive solution to ensure pages always start scrolled at the top
 * Uses multiple techniques to ensure maximum compatibility
 */
function ScrollRestoration() {
  const { pathname } = useLocation();
  
  // First technique: Use useLayoutEffect (runs before browser paint)
  useLayoutEffect(() => {
    // Scroll with multiple methods for maximum compatibility
    window.scrollTo(0, 0);
    document.documentElement.scrollTo(0, 0);
    document.body.scrollTo(0, 0);
    
    // Alternative method with options
    try {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'auto' // 'auto' is most reliable
      });
    } catch (error) {
      // Fallback for older browsers
      window.scrollTo(0, 0);
    }
  }, [pathname]);
  
  // Second technique: Use regular useEffect as backup
  useEffect(() => {
    // Extra backup with slight delay
    const timeoutId = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
    
    return () => clearTimeout(timeoutId);
  }, [pathname]);
  
  // Third technique: Handle document events
  useEffect(() => {
    const handleLoad = () => {
      window.scrollTo(0, 0);
    };
    
    window.addEventListener('load', handleLoad);
    
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, []);
  
  // Fourth technique: Override history methods
  useEffect(() => {
    // Store original methods
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    
    // Override pushState
    window.history.pushState = function() {
      originalPushState.apply(this, arguments);
      window.scrollTo(0, 0);
    };
    
    // Override replaceState
    window.history.replaceState = function() {
      originalReplaceState.apply(this, arguments);
      window.scrollTo(0, 0);
    };
    
    return () => {
      // Restore original methods when component unmounts
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, []);
  
  return null; // This component doesn't render anything
}

export default ScrollRestoration;